import React, { ReactNode } from 'react';

// helpers
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { IGroupParticipantShort } from 'typings/approvalManagement/accountManagement';
import { FormikHelpers, FormikProps } from 'formik';
import { ApprovalGroupValidationSchema } from 'validations/accountManagement/approvalGroups';

// components
import {
  Button,
  AutocompleteOption,
  Form,
  RequiredPropsForFormModel,
  ModalDialog,
  RequiredPropsForModalDialogModel,
} from '@ui';
import InnerForm from './InnerForm';
import LoadingWrapper from '../../../../WrapperComponents/LoadingWrapper';

export interface ApprovalGroupFormValuesModel {
  name: string;
  disabled?: boolean;
  description: string | undefined;
  participants: string[];
  initialParticipantOptions?: AutocompleteOption<IGroupParticipantShort>[];
}

export type ApprovalWorkflowType =
  | 'transactions'
  | 'account-management-transactions'
  | 'onboarding-approval-group';

interface IProps
  extends RequiredPropsForModalDialogModel,
    RequiredPropsForFormModel<ApprovalGroupFormValuesModel> {
  title: ReactNode;
  approvalWorkflowType: ApprovalWorkflowType;
  isEditMode?: boolean;
  loadingInitialData?: boolean;
  onSubmit: (
    values: ApprovalGroupFormValuesModel,
    helpers: FormikHelpers<ApprovalGroupFormValuesModel>,
    isUpdated?: boolean,
  ) => void | Promise<unknown>;
  onDelete?: (form: FormikProps<ApprovalGroupFormValuesModel>) => Promise<void>;
}

const ApprovalGroupModal = ({
  title,
  isVisible,
  closeCallback,
  initialValues,
  disabled,
  approvalWorkflowType,
  loadingInitialData,
  isEditMode,
  onDelete,
  onSubmit,
}: IProps) => {
  const { t } = useTranslation('common');

  const handleSubmit = async (
    values: ApprovalGroupFormValuesModel,
    helpers: FormikHelpers<ApprovalGroupFormValuesModel>,
  ) => {
    const isDirty = !_.isEqual(values, initialValues);
    await onSubmit(values, helpers, isDirty);
  };

  return (
    <Form
      enableReinitialize
      disabled={disabled}
      onSubmit={handleSubmit}
      initialValues={isVisible ? initialValues : null}
      validationSchema={ApprovalGroupValidationSchema}
      renderForm={(form) => (
        <ModalDialog
          title={title}
          isVisible={isVisible}
          closeCallback={closeCallback}
          extendedFooterButtonsPosition={onDelete ? 'before' : undefined}
          extendedFooterButtons={
            onDelete ? (
              <Button
                danger
                size="large"
                type="bordered"
                onClick={() => onDelete(form)}
              >
                {t('delete', { ns: 'common' })}
              </Button>
            ) : null
          }
        >
          <LoadingWrapper loading={!!loadingInitialData}>
            <InnerForm
              approvalWorkflowType={approvalWorkflowType}
              isEditMode={isEditMode}
            />
          </LoadingWrapper>
        </ModalDialog>
      )}
    />
  );
};

export default ApprovalGroupModal;
